import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Loader } from '@/components/ui/Loader';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerSoft';
type Size = 'sm' | 'md' | 'lg';

const SPINNER_SIZE: Record<Size, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

/**
 * Only solid tokens here — no `/opacity` modifiers.
 *
 * The palette is declared as `var(--crit)` and the like, which Tailwind cannot
 * add an alpha channel to: a class such as `bg-crit/10` compiles to nothing at
 * all and the element simply renders unstyled. Every shade below is a token
 * that exists in its own right.
 */
const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent border-accent text-white hover:bg-accent-2 hover:border-accent-2',
  secondary: 'bg-surface border-line-strong text-ink hover:border-accent hover:text-accent',
  ghost:
    'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700',
  /**
   * The committing destructive action — the confirm button in a dialog.
   * Solid red, because this is the click that actually does the thing and it
   * should never be mistaken for the cancel beside it.
   */
  danger: 'bg-crit border-crit text-white hover:opacity-90',
  /**
   * The destructive action *offered* in a row or a toolbar.
   *
   * Tinted rather than solid: a table of twenty rows each carrying a solid red
   * button is a wall of alarm, and nothing reads as more urgent than anything
   * else. It still reads unmistakably red at rest — which is the point, an
   * action that only turns red on hover is one people click by accident — and
   * fills solid on hover to confirm what it is about to do.
   */
  dangerSoft: 'bg-crit-bg border-crit text-crit hover:bg-crit hover:text-white',
};

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-10 px-5 text-sm gap-2',
};

/** A square at the size's own height, for a button that is just a glyph. */
const ICON_ONLY: Record<Size, string> = {
  sm: 'w-8 px-0',
  md: 'w-10 px-0',
  lg: 'w-10 px-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  loaderType?: 'book' | 'spinner';
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /** A square holding only its icon. Give it an `aria-label` and a `title`. */
  iconOnly?: boolean;
}

/**
 * The button's look, for an element that cannot be a `<button>`.
 *
 * A `tel:` or `mailto:` action has to be an `<a>` to work, and copying the
 * classes below into it is how two "identical" buttons drift apart. `Button`
 * itself is built from this, so the two cannot differ.
 */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  iconOnly = false,
}: { variant?: Variant; size?: Size; fullWidth?: boolean; iconOnly?: boolean } = {}): string {
  return cn(
    'inline-flex items-center justify-center rounded-field border font-[560]',
    'transition-colors duration-150',
    'disabled:cursor-not-allowed disabled:opacity-55',
    VARIANT[variant],
    SIZE[size],
    fullWidth && 'w-full',
    // Icon only means no visible name, so the caller must give an `aria-label`
    // and a `title` saying who or what the glyph reaches.
    iconOnly && ICON_ONLY[size],
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    loaderType = 'book',
    fullWidth = false,
    leftIcon,
    rightIcon,
    iconOnly = false,
    disabled,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // A loading button must stay unclickable, or an impatient user submits twice.
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      className={cn(buttonClassName({ variant, size, fullWidth, iconOnly }), className)}
      {...props}
    >
      {isLoading ? (
        loaderType === 'spinner' ? (
          <Loader2 className={cn('animate-spin shrink-0', SPINNER_SIZE[size])} aria-hidden />
        ) : (
          <Loader size="sm" />
        )
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
});
